import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { useTranslation } from 'react-i18next';

/**
 * Hero section component for the homepage
 * Features a prominent headline, description, and call-to-action
 * Designed with a vibrant, gaming-friendly approach
 * Supports multiple languages through i18n
 */
const Hero = () => {
  const { t } = useTranslation();
  const heroRef = useRef(null);
  const textRef = useRef(null);
  const lineRef = useRef(null);
  
  // GSAP animations
  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animate the line
      gsap.from(lineRef.current, {
        width: 0,
        duration: 1.2,
        ease: "power3.out",
        delay: 0.3
      });
      
      // Animate the text elements
      const textElements = textRef.current.querySelectorAll('h1, p, div');
      gsap.from(textElements, {
        y: 40,
        opacity: 0,
        duration: 0.8,
        stagger: 0.15,
        ease: "back.out(1.2)",
        delay: 0.5
      });
    }, heroRef);
    
    return () => ctx.revert();
  }, []);
  
  return (
    <section 
      ref={heroRef}
      className="relative bg-primary text-white overflow-hidden min-h-screen flex items-center"
    >
      {/* Solid background with subtle pattern */}
      <div className="absolute inset-0 bg-primary">
        <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC44Ij48cGF0aCBkPSJNMzYgMzRjMC0yLjIgMS44LTQgNC00czQgMS44IDQgNC0xLjggNC00IDQtNC0xLjgtNC00eiIvPjwvZz48L2c+PC9zdmc+')]"></div>
      </div>
      
      {/* Animated background elements */}
      <motion.div 
        className="absolute top-1/4 right-1/4 w-64 h-64 rounded-full bg-secondary/10"
        animate={{
          x: [0, 30, 0],
          y: [0, -30, 0],
          scale: [1, 1.05, 1],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      
      <motion.div 
        className="absolute bottom-1/4 left-1/4 w-40 h-40 rounded-full bg-accent/10"
        animate={{
          x: [0, -20, 0],
          y: [0, 20, 0],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      
      {/* Pixel art style decorative elements */}
      <div className="absolute top-20 right-20 w-8 h-8 bg-accent rotate-45"></div>
      <div className="absolute bottom-32 left-16 w-6 h-6 bg-secondary rotate-12"></div>
      <div className="absolute top-40 left-24 w-4 h-4 bg-accent/70"></div>
      
      <div className="container mx-auto px-6 pt-32 pb-20 md:pt-40 md:pb-32 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <div ref={textRef}>
            <div ref={lineRef} className="w-24 h-2 bg-accent rounded-full mb-10"></div>
            
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-8 leading-tight">
              {t('hero.title').split(' ').map((word, index, array) => 
                index === array.length - 1 ? 
                  <span key={index}><span className="text-accent">{word}</span></span> : 
                  <span key={index}>{word} </span>
              )}
            </h1>
            
            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-12 max-w-2xl">
              {t('hero.description')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6">
              <Link 
                to="/contact" 
                className="btn bg-accent text-primary px-8 py-4 rounded-md shadow-lg hover:shadow-accent/30 hover:bg-white"
              >
                {t('hero.getQuote')}
              </Link>
              <Link 
                to="/portfolio" 
                className="btn-outline border-2 border-white text-white px-8 py-4 rounded-md hover:bg-white/10"
              >
                {t('hero.viewWork')}
              </Link>
            </div>
            
            {/* Gaming badge */}
            <div className="mt-12 inline-block pixel-border bg-primary px-4 py-2">
              <span className="text-accent font-medium">{t('hero.trustedBy')}</span>
            </div>
          </div>
          
          {/* Banner Image */}
          <div className="hidden lg:block relative">
            <div className="relative rounded-lg overflow-hidden shadow-xl">
              <img 
                src="/natux-banner.jpg" 
                alt="NatuX Translations Banner" 
                className="w-full h-auto"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-primary/40 to-transparent"></div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Scroll indicator */}
      <motion.div 
        className="absolute bottom-10 left-1/2 transform -translate-x-1/2 flex flex-col items-center"
        animate={{
          y: [0, 10, 0],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      >
        <span className="text-white/70 text-sm uppercase tracking-widest mb-2">{t('hero.scroll')}</span>
        <svg width="20" height="30" viewBox="0 0 20 30" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="1" y="1" width="18" height="28" rx="9" stroke="white" strokeOpacity="0.7" strokeWidth="2"/>
          <circle className="animate-bounce" cx="10" cy="10" r="4" fill="white" fillOpacity="0.7"/>
        </svg>
      </motion.div>
    </section>
  );
};

export default Hero;
